/**
 * Surfaces widget-registration warnings as model diagnostics.
 *
 * Stdlib + user widget bundles register `arch-*` custom elements at
 * import time; a duplicate or throwing `define()` is caught by
 * `widgetGuard` and recorded as a `WidgetDefineWarning` instead of
 * crashing the viewer (see `board/widgetGuard.ts`). This hook subscribes
 * to those warnings and folds them into the diagnostics stream so the
 * count badge and diagnostics panel show them alongside model issues.
 *
 * Widget warnings carry no source span, so they are *not* attributed to
 * any module — per-module badges keep using the model diagnostics only.
 */

import { useEffect, useMemo, useState } from "react";
import type { Diagnostic } from "@archlang/scene/arch-model";
import { onWidgetDefineWarning, type WidgetDefineWarning } from "../board/widgetGuard";

export function useWidgetDiagnostics(
  modelDiagnostics: readonly Diagnostic[],
): readonly Diagnostic[] {
  const [widgetWarnings, setWidgetWarnings] = useState<readonly WidgetDefineWarning[]>([]);
  useEffect(() => onWidgetDefineWarning((w) => setWidgetWarnings([...w])), []);

  const widgetDiagnostics = useMemo<readonly Diagnostic[]>(
    () =>
      widgetWarnings.map((w) => ({
        severity: "warning" as const,
        message: w.message,
        code: w.reason === "duplicate" ? "WIDGET_DUPLICATE" : "WIDGET_DEFINE_ERROR",
      })),
    [widgetWarnings],
  );

  return useMemo<readonly Diagnostic[]>(
    () =>
      widgetDiagnostics.length
        ? [...modelDiagnostics, ...widgetDiagnostics]
        : modelDiagnostics,
    [modelDiagnostics, widgetDiagnostics],
  );
}
